import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import Hls from 'hls.js';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Minimize, 
  ArrowLeft, 
  Tv, 
  RefreshCw, 
  Settings, 
  List, 
  ChevronLeft, 
  ChevronRight, 
  AlertCircle, 
  Star,
  Layers,
  Radio,
  ExternalLink,
  Copy,
  Check,
  Share2
} from 'lucide-react';
import { IptvChannel, IptvVodMovie, IptvEpisode } from '../../types';
import { getProxiedStreamUrl, getProxiedImageUrl } from '../../services/iptvService';
import { ChannelLogo } from './ChannelLogo';
import { MarqueeText } from './MarqueeText';
import { isBackEvent } from '../../utils/deviceDetection';

interface IptvPlayerProps {
  item: IptvChannel | IptvVodMovie | IptvEpisode;
  allChannels?: IptvChannel[];
  onClose: () => void;
  onSelectChannel?: (channel: IptvChannel) => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

type PlaybackEngine = 'hls' | 'native' | 'external';
type StreamFormat = 'auto' | 'm3u8' | 'ts' | 'direct';
type AspectRatioOption = 'contain' | 'cover' | 'fill' | '16:9' | '4:3';

export const IptvPlayer: React.FC<IptvPlayerProps> = ({
  item,
  allChannels = [],
  onClose,
  onSelectChannel,
  isFavorite = false,
  onToggleFavorite
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showChannelList, setShowChannelList] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<AspectRatioOption>('contain');
  const [isLoading, setIsLoading] = useState(true);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [useDirectUrl, setUseDirectUrl] = useState(false);
  const [selectedUserAgent, setSelectedUserAgent] = useState<string>((item as any).userAgent || (item as any).customUserAgent || 'IPTVSmartersPro');
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [activeEngine, setActiveEngine] = useState<PlaybackEngine>('hls');
  const [selectedFormat, setSelectedFormat] = useState<StreamFormat>('auto');

  const isLive = !('duration' in item);
  const channelIndex = allChannels.findIndex(c => c.id === item.id);
  const currentChannel = isLive ? (item as IptvChannel) : null;
  const itemName = ('name' in item && item.name) || ('title' in item && (item as any).title) || 'Emissió';
  const itemSourceName = (item as any).sourceName;
  const customReferer = (item as any).referer || (item as any).customReferer || '';

  // Compute alternative stream format URLs (.m3u8, .ts, direct)
  const rawStreamUrl = item.directStreamUrl || item.streamUrl || '';

  const formatUrls = useMemo(() => {
    let m3u8Url = rawStreamUrl;
    let tsUrl = rawStreamUrl;

    if (rawStreamUrl.includes('/live/') && !rawStreamUrl.includes('.m3u8') && !rawStreamUrl.includes('.ts')) {
      m3u8Url = `${rawStreamUrl}.m3u8`;
      tsUrl = `${rawStreamUrl}.ts`;
    } else if (rawStreamUrl.endsWith('.ts')) {
      m3u8Url = rawStreamUrl.replace(/\.ts$/i, '.m3u8');
    } else if (rawStreamUrl.endsWith('.m3u8')) {
      tsUrl = rawStreamUrl.replace(/\.m3u8$/i, '.ts');
    }

    return { m3u8Url, tsUrl, raw: rawStreamUrl };
  }, [rawStreamUrl]);

  // Compute active target stream URL
  const targetRawUrl = useMemo(() => {
    if (selectedFormat === 'm3u8') return formatUrls.m3u8Url;
    if (selectedFormat === 'ts') return formatUrls.tsUrl;
    return rawStreamUrl;
  }, [selectedFormat, formatUrls, rawStreamUrl]);

  const activeStreamUrl = useMemo(() => {
    if (useDirectUrl) return targetRawUrl;
    return getProxiedStreamUrl(targetRawUrl, selectedUserAgent);
  }, [useDirectUrl, targetRawUrl, selectedUserAgent]);

  // External Player launcher (Android Intent / VLC / MX Player)
  const handleLaunchExternalPlayer = useCallback(() => {
    const rawUrl = targetRawUrl;
    const title = itemName;
    const ua = selectedUserAgent;
    const ref = customReferer;

    // 1. Check for native Android WebAppInterface bridge
    const win = window as any;
    if (win.Android && typeof win.Android.playInExternalPlayer === 'function') {
      win.Android.playInExternalPlayer(rawUrl, title, ua, ref);
      return;
    }
    if (win.Android && typeof win.Android.playMedia === 'function') {
      win.Android.playMedia(item.id, title, title, rawUrl, (item as any).logo || (item as any).poster || '', 1, 0);
      return;
    }
    if (win.AndroidExoBridge && typeof win.AndroidExoBridge.playMedia === 'function') {
      win.AndroidExoBridge.playMedia(item.id, title, title, rawUrl, (item as any).logo || (item as any).poster || '', 1, 0);
      return;
    }

    // 2. Try Android intent scheme if on mobile/tablet browser
    const isAndroid = /Android/i.test(navigator.userAgent);
    if (isAndroid) {
      let intentUrl = `intent:${rawUrl}#Intent;type=video/*;package=org.videolan.vlc;S.title=${encodeURIComponent(title)};S.http-user-agent=${encodeURIComponent(ua)}`;
      if (ref) {
        intentUrl += `;S.Referer=${encodeURIComponent(ref)}`;
      }
      intentUrl += `;end`;
      window.location.href = intentUrl;
      return;
    }

    // 3. Fallback to vlc:// protocol or copy link
    const vlcUrl = `vlc://${rawUrl}`;
    window.open(vlcUrl, '_blank');
  }, [targetRawUrl, itemName, selectedUserAgent, customReferer, item]);

  // Native ExoPlayer Launcher (Hardware accelerated on Android TV / APK)
  const handleLaunchNativePlayer = useCallback(() => {
    const rawUrl = targetRawUrl;
    const title = itemName;
    const poster = (item as any).logo || (item as any).poster || '';
    const ua = selectedUserAgent;
    const ref = customReferer;
    const win = window as any;

    if (win.Android && typeof win.Android.playIptvStream === 'function') {
      win.Android.playIptvStream(rawUrl, title, poster, ua, ref);
      return;
    }
    if (win.Android && typeof win.Android.playMedia === 'function') {
      win.Android.playMedia(item.id, title, title, rawUrl, poster, 1, 0);
      return;
    }
    if (win.AndroidExoBridge && typeof win.AndroidExoBridge.playMedia === 'function') {
      win.AndroidExoBridge.playMedia(item.id, title, title, rawUrl, poster, 1, 0);
      return;
    }
    handleLaunchExternalPlayer();
  }, [targetRawUrl, itemName, item, selectedUserAgent, customReferer, handleLaunchExternalPlayer]);

  // Copy Stream URL to Clipboard
  const handleCopyStreamUrl = () => {
    navigator.clipboard.writeText(targetRawUrl).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2500);
    });
  };

  // Initialize and load player
  const initPlayer = useCallback(() => {
    if (!videoRef.current || !activeStreamUrl) return;

    setIsLoading(true);
    setStreamError(null);

    // Destroy existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const video = videoRef.current;
    const isM3u8Like = activeStreamUrl.toLowerCase().includes('.m3u8') || activeStreamUrl.includes('m3u8') || isLive;

    // Mode A: HLS.js Engine
    if (activeEngine === 'hls' && Hls.isSupported() && isM3u8Like) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 60,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,
        nudgeMaxRetry: 10,
        nudgeOffset: 0.1,
        fragLoadingTimeOut: 20000,
        manifestLoadingTimeOut: 20000,
        xhrSetup: (xhr) => {
          xhr.withCredentials = false;
        }
      });

      hlsRef.current = hls;
      hls.loadSource(activeStreamUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsLoading(false);
        video.play().then(() => setIsPlaying(true)).catch(() => {
          setIsPlaying(false);
        });
      });

      let mediaRecoveryAttempts = 0;
      let networkRecoveryAttempts = 0;

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              networkRecoveryAttempts++;
              console.warn(`HLS Network Error (${data.details}), intent #${networkRecoveryAttempts}`);
              if (networkRecoveryAttempts === 1) {
                hls.startLoad();
              } else if (networkRecoveryAttempts === 2) {
                setTimeout(() => {
                  if (hlsRef.current) hlsRef.current.startLoad();
                }, 1000);
              } else {
                // If M3U8 failed and we haven't tried TS yet, attempt TS format fallback
                if (selectedFormat === 'auto' && formatUrls.tsUrl !== targetRawUrl) {
                  console.warn('HLS manifest load failed, attempting TS stream format fallback...');
                  setSelectedFormat('ts');
                  return;
                }

                // Fallback to native video
                console.warn('HLS network recovery exceeded, attempting native video fallback...');
                hls.destroy();
                hlsRef.current = null;
                if (video) {
                  video.src = activeStreamUrl;
                  video.load();
                  video.play().then(() => {
                    setIsLoading(false);
                    setIsPlaying(true);
                  }).catch(() => {
                    setStreamError(`No s'ha pogut carregar l'emissió (${data.details || 'Error de xarxa o emissió fora de línia'}). Comprova si l'enllaç està actiu o utilitza un reproductor extern.`);
                    setIsLoading(false);
                  });
                }
              }
              break;

            case Hls.ErrorTypes.MEDIA_ERROR:
              mediaRecoveryAttempts++;
              console.warn(`HLS Media Error (${data.details}), intent #${mediaRecoveryAttempts}`);
              if (mediaRecoveryAttempts === 1) {
                hls.recoverMediaError();
              } else if (mediaRecoveryAttempts === 2) {
                hls.swapAudioCodec();
                hls.recoverMediaError();
              } else {
                console.warn('HLS unrecoverable media error, reloading...');
                hls.destroy();
                hlsRef.current = null;
                setTimeout(() => setRetryCount(c => c + 1), 500);
              }
              break;

            default:
              console.error('HLS Unrecoverable Error:', data);
              hls.destroy();
              hlsRef.current = null;
              if (video) {
                video.src = activeStreamUrl;
                video.load();
                video.play().then(() => {
                  setIsLoading(false);
                  setIsPlaying(true);
                }).catch(() => {
                  setStreamError(`Error en reproduir la transmissió (${data.details || 'Format no suportat pel navegador'}).`);
                  setIsLoading(false);
                });
              }
              break;
          }
        }
      });
    } else {
      // Mode B: Native Video Engine (Direct HTML5 / Safari / iOS / WebM / MP4)
      video.src = activeStreamUrl;
      video.load();
      video.play().then(() => {
        setIsLoading(false);
        setIsPlaying(true);
      }).catch((err) => {
        console.warn('Native video play error:', err);
        setStreamError('Error en reproduir amb el motor natiu. Moltes emissions IPTV requereixen un reproductor extern (VLC / MX Player) si utilitzen còdecs propietaris.');
        setIsLoading(false);
      });
    }
  }, [activeStreamUrl, activeEngine, isLive, selectedFormat, formatUrls, targetRawUrl]);

  useEffect(() => {
    initPlayer();
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [initPlayer, retryCount]);

  // Controls Visibility Auto-hide
  const triggerControls = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying && !showChannelList && !showSettingsModal) {
        setShowControls(false);
      }
    }, 4500);
  }, [isPlaying, showChannelList, showSettingsModal]);

  useEffect(() => {
    triggerControls();
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [triggerControls]);

  // Channel Zapping Handlers
  const zapNextChannel = useCallback(() => {
    if (!allChannels.length || channelIndex === -1) return;
    const nextIdx = (channelIndex + 1) % allChannels.length;
    onSelectChannel?.(allChannels[nextIdx]);
  }, [allChannels, channelIndex, onSelectChannel]);

  const zapPrevChannel = useCallback(() => {
    if (!allChannels.length || channelIndex === -1) return;
    const prevIdx = (channelIndex - 1 + allChannels.length) % allChannels.length;
    onSelectChannel?.(allChannels[prevIdx]);
  }, [allChannels, channelIndex, onSelectChannel]);

  // Expose back handler to window for global back dispatchers
  useEffect(() => {
    (window as any).__handleIptvPlayerBack = () => {
      if (showSettingsModal) {
        setShowSettingsModal(false);
        return true;
      }
      if (showChannelList) {
        setShowChannelList(false);
        return true;
      }
      onClose();
      return true;
    };
    return () => {
      delete (window as any).__handleIptvPlayerBack;
    };
  }, [showSettingsModal, showChannelList, onClose]);

  // D-Pad and Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      triggerControls();

      // Intercept all Back button variations (Escape, Backspace, Android Back, TV Remote Back)
      if (isBackEvent(e)) {
        e.preventDefault();
        e.stopPropagation();
        if (showSettingsModal) {
          setShowSettingsModal(false);
        } else if (showChannelList) {
          setShowChannelList(false);
        } else {
          onClose();
        }
        return;
      }

      switch (e.key) {
        case 'Escape':
        case 'Backspace':
        case 'BrowserBack':
          if (showSettingsModal) {
            setShowSettingsModal(false);
          } else if (showChannelList) {
            setShowChannelList(false);
          } else {
            onClose();
          }
          break;
        case ' ':
        case 'k':
          e.preventDefault();
          if (videoRef.current) {
            if (videoRef.current.paused) {
              videoRef.current.play();
              setIsPlaying(true);
            } else {
              videoRef.current.pause();
              setIsPlaying(false);
            }
          }
          break;
        case 'PageUp':
        case 'ChannelUp':
          if (isLive) {
            e.preventDefault();
            zapPrevChannel();
          }
          break;
        case 'PageDown':
        case 'ChannelDown':
          if (isLive) {
            e.preventDefault();
            zapNextChannel();
          }
          break;
        case 'ArrowUp':
          if (!showChannelList && !showSettingsModal) {
            // Volume adjustment only if not navigating inside active modals
            if (document.activeElement?.id === 'iptv-player-play-btn') {
              e.preventDefault();
              setVolume(v => {
                const newVol = Math.min(1, v + 0.1);
                if (videoRef.current) videoRef.current.volume = newVol;
                return newVol;
              });
            }
          }
          break;
        case 'ArrowDown':
          if (!showChannelList && !showSettingsModal) {
            if (document.activeElement?.id === 'iptv-player-play-btn') {
              e.preventDefault();
              setVolume(v => {
                const newVol = Math.max(0, v - 0.1);
                if (videoRef.current) videoRef.current.volume = newVol;
                return newVol;
              });
            }
          }
          break;
        case 'f':
          toggleFullscreen();
          break;
        case 'c':
        case 'l':
          if (isLive) setShowChannelList(prev => !prev);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [triggerControls, isLive, zapPrevChannel, zapNextChannel, showChannelList, showSettingsModal, onClose]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  const cycleAspectRatio = () => {
    const modes: AspectRatioOption[] = ['contain', 'cover', 'fill', '16:9', '4:3'];
    const next = modes[(modes.indexOf(aspectRatio) + 1) % modes.length];
    setAspectRatio(next);
  };

  const getAspectRatioClasses = () => {
    switch (aspectRatio) {
      case 'cover':
        return 'w-full h-full object-cover';
      case 'fill':
        return 'w-full h-full object-fill';
      case '16:9':
        return 'w-full max-w-full aspect-video object-contain';
      case '4:3':
        return 'w-full max-w-full aspect-[4/3] object-contain';
      case 'contain':
      default:
        return 'w-full h-full object-contain';
    }
  };

  const getAspectRatioLabel = () => {
    switch (aspectRatio) {
      case 'cover':
        return 'Zoom / Omplir';
      case 'fill':
        return 'Estirar';
      case '16:9':
        return '16:9';
      case '4:3':
        return '4:3';
      case 'contain':
      default:
        return 'Ajustar (Fit)';
    }
  };

  return (
    <div
      id="iptv-video-player"
      ref={containerRef}
      onMouseMove={triggerControls}
      onClick={triggerControls}
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center overflow-hidden select-none font-sans"
    >
      {/* HTML5 Video Element */}
      <video
        ref={videoRef}
        playsInline
        autoPlay
        crossOrigin="anonymous"
        className={getAspectRatioClasses()}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsLoading(true)}
        onPlaying={() => setIsLoading(false)}
      />

      {/* Loading Spinner */}
      {isLoading && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-none p-4 text-center">
          <RefreshCw className="w-12 h-12 text-red-500 animate-spin mb-3" />
          <p className="text-white text-sm font-semibold tracking-wide">
            {isLive ? 'Connectant amb l\'emissió en directe...' : 'Carregant contingut...'}
          </p>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
            <span className="px-2 py-0.5 rounded bg-slate-800 font-mono">
              {activeEngine === 'hls' ? 'Motor HLS.js' : 'Motor Natiu'}
            </span>
            <span>•</span>
            <span className="px-2 py-0.5 rounded bg-slate-800 font-mono">
              {useDirectUrl ? 'Enllaç Directe' : `Proxy (${selectedUserAgent})`}
            </span>
          </div>
        </div>
      )}

      {/* Stream Error & Fallback Overlay */}
      {streamError && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/95 p-6 text-center overflow-y-auto">
          <div className="max-w-md w-full flex flex-col items-center">
            <AlertCircle className="w-14 h-14 text-red-500 mb-3 animate-pulse" />
            <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Problema amb la reproducció</h3>
            <p className="text-xs sm:text-sm text-slate-300 mb-4 bg-slate-900/90 border border-white/10 rounded-xl p-3 text-left w-full font-mono">
              {streamError}
            </p>

            {/* Quick External Player Action (High Priority Recommendation) */}
            <div className="w-full bg-gradient-to-r from-red-950/60 to-slate-900 border border-red-500/40 rounded-2xl p-4 mb-4 text-left space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-red-400 uppercase tracking-wider flex items-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5" />
                  Solució ràpida recomanada:
                </span>
                <span className="text-[10px] bg-red-600/30 text-red-300 px-2 py-0.5 rounded-full font-semibold">
                  Sense límit de còdecs
                </span>
              </div>
              <p className="text-xs text-slate-300">
                Molts canals IPTV utilitzen formats d&apos;àudio/vídeo que els navegadors web no admeten directament. Obre el canal a <strong>VLC</strong> o <strong>MX Player</strong> per reproduir-lo a l&apos;instant:
              </p>
              
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
                {(typeof window !== 'undefined' && Boolean((window as any).Android)) && (
                  <button
                    onClick={handleLaunchNativePlayer}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-bold rounded-xl text-xs sm:text-sm shadow-lg shadow-emerald-600/30 transition-all"
                  >
                    <Play className="w-4 h-4 fill-current" />
                    Reproductor Natiu (ExoPlayer)
                  </button>
                )}

                <button
                  onClick={handleLaunchExternalPlayer}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 active:scale-95 text-white font-bold rounded-xl text-xs sm:text-sm shadow-lg shadow-red-600/30 transition-all"
                >
                  <Play className="w-4 h-4 fill-current" />
                  Obrir a VLC / MX Player
                </button>

                <button
                  onClick={handleCopyStreamUrl}
                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-200 font-semibold rounded-xl text-xs border border-slate-700 transition-all"
                  title="Copiar enllaç directe"
                >
                  {copiedUrl ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  {copiedUrl ? 'Copiat!' : 'Copiar'}
                </button>
              </div>
            </div>

            {/* In-Browser Recovery Controls */}
            <div className="w-full bg-slate-900/60 border border-slate-800 rounded-xl p-3 mb-4 text-left">
              <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Canviar format o mode del flux:
              </label>
              <div className="grid grid-cols-2 gap-1.5 mb-2">
                <button
                  onClick={() => {
                    setSelectedFormat('m3u8');
                    setStreamError(null);
                    setRetryCount(c => c + 1);
                  }}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    selectedFormat === 'm3u8' ? 'bg-red-600 text-white font-bold' : 'bg-slate-800 text-slate-300'
                  }`}
                >
                  Format HLS (.m3u8)
                </button>
                <button
                  onClick={() => {
                    setSelectedFormat('ts');
                    setStreamError(null);
                    setRetryCount(c => c + 1);
                  }}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    selectedFormat === 'ts' ? 'bg-red-600 text-white font-bold' : 'bg-slate-800 text-slate-300'
                  }`}
                >
                  Format TS (.ts)
                </button>
              </div>

              <div className="grid grid-cols-2 gap-1.5">
                <button
                  onClick={() => {
                    setUseDirectUrl(d => !d);
                    setStreamError(null);
                    setRetryCount(c => c + 1);
                  }}
                  className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium border border-slate-700 text-center"
                >
                  {useDirectUrl ? 'Activar Proxy CORS' : 'Provar Enllaç Directe'}
                </button>
                <button
                  onClick={() => {
                    setActiveEngine(e => e === 'hls' ? 'native' : 'hls');
                    setStreamError(null);
                    setRetryCount(c => c + 1);
                  }}
                  className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium border border-slate-700 text-center"
                >
                  {activeEngine === 'hls' ? 'Canviar a Motor Natiu' : 'Canviar a Motor HLS'}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 w-full">
              <button
                onClick={() => {
                  setStreamError(null);
                  setRetryCount(c => c + 1);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl text-xs border border-slate-700 transition-all"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Reintentar
              </button>

              <button
                onClick={onClose}
                className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white rounded-xl text-xs transition-colors"
              >
                Sortir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Channel Switcher Sidebar / Drawer */}
      {showChannelList && isLive && allChannels.length > 0 && (
        <div 
          role="dialog"
          aria-modal="true"
          id="iptv-channel-guide-drawer"
          className="absolute inset-y-0 left-0 z-40 w-80 sm:w-96 bg-slate-950/95 backdrop-blur-xl border-r border-slate-800 p-4 flex flex-col shadow-2xl animate-in slide-in-from-left duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-800">
            <h3 className="font-bold text-white flex items-center gap-2 text-sm">
              <Tv className="w-4 h-4 text-red-500" />
              Guia de Canals ({allChannels.length})
            </h3>
            <button
              id="iptv-channel-guide-close-btn"
              data-focusable="true"
              type="button"
              onClick={() => setShowChannelList(false)}
              className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors text-xs font-semibold px-2 py-1 outline-none focus:ring-2 focus:ring-red-500"
            >
              Tancar
            </button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 scrollbar-thin scrollbar-thumb-slate-800">
            {allChannels.map((ch, idx) => {
              const isSelected = ch.id === item.id;
              return (
                <button
                  key={ch.id}
                  id={`iptv-guide-channel-${idx}`}
                  data-focusable="true"
                  type="button"
                  onClick={() => {
                    onSelectChannel?.(ch);
                    setShowChannelList(false);
                  }}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-all outline-none focus:ring-2 focus:ring-red-500 ${
                    isSelected
                      ? 'bg-red-600/20 text-white border border-red-500/40 shadow-md shadow-red-600/10'
                      : 'text-slate-300 hover:bg-slate-900 hover:text-white border border-transparent'
                  }`}
                >
                  <span className="text-xs font-bold font-mono text-slate-500 w-6 text-right shrink-0">
                    {ch.number || idx + 1}
                  </span>

                  <ChannelLogo
                    logo={ch.logo}
                    name={ch.name}
                    category={ch.category}
                    userAgent={selectedUserAgent}
                    size="sm"
                    className="shrink-0"
                  />

                  <div className="flex-1 min-w-0">
                    <MarqueeText text={ch.name} className="font-medium text-xs leading-snug text-slate-200" />
                    <div className="text-[10px] text-slate-400 truncate">{ch.category}</div>
                  </div>

                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Settings Modal (User-Agent, Format, Engine) */}
      {showSettingsModal && (
        <div 
          role="dialog"
          aria-modal="true"
          id="iptv-player-settings-modal"
          className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
          onClick={(e) => { e.stopPropagation(); setShowSettingsModal(false); }}
        >
          <div 
            className="max-w-md w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4 text-left shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="font-bold text-white flex items-center gap-2 text-sm">
                <Settings className="w-4 h-4 text-red-500" />
                Configuració del Reproductor
              </h3>
              <button
                id="iptv-settings-close-btn"
                data-focusable="true"
                type="button"
                onClick={() => setShowSettingsModal(false)}
                className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Tancar
              </button>
            </div>

            {/* Agent d'usuari */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300 block">
                Agent d&apos;Usuari (User-Agent):
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { label: 'IPTVSmartersPro (Per defecte)', value: 'IPTVSmartersPro' },
                  { label: 'VLC Media Player', value: 'VLC/3.0.18 LibVLC/3.0.18' },
                  { label: 'TiviMate', value: 'TiviMate/4.7.0' },
                  { label: 'okhttp (Android)', value: 'okhttp/4.9.0' },
                ].map((ua, uaIdx) => (
                  <button
                    key={ua.label}
                    id={`iptv-setting-ua-${uaIdx}`}
                    data-focusable="true"
                    type="button"
                    onClick={() => {
                      setSelectedUserAgent(ua.value);
                      setRetryCount(c => c + 1);
                    }}
                    className={`px-3 py-2 rounded-lg text-xs font-medium text-left truncate transition-all outline-none focus:ring-2 focus:ring-red-500 ${
                      selectedUserAgent === ua.value
                        ? 'bg-red-600 text-white font-bold'
                        : 'bg-slate-900 text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    {ua.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Format del flux */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300 block">
                Format d&apos;Emissió:
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {(['auto', 'm3u8', 'ts'] as StreamFormat[]).map((fmt, fmtIdx) => (
                  <button
                    key={fmt}
                    id={`iptv-setting-fmt-${fmtIdx}`}
                    data-focusable="true"
                    type="button"
                    onClick={() => {
                      setSelectedFormat(fmt);
                      setRetryCount(c => c + 1);
                    }}
                    className={`px-3 py-2 rounded-lg text-xs font-medium uppercase transition-all outline-none focus:ring-2 focus:ring-red-500 ${
                      selectedFormat === fmt
                        ? 'bg-red-600 text-white font-bold'
                        : 'bg-slate-900 text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    {fmt === 'auto' ? 'Automàtic' : fmt === 'm3u8' ? 'HLS (.m3u8)' : 'TS (.ts)'}
                  </button>
                ))}
              </div>
            </div>

            {/* External Player Hand-off */}
            <div className="pt-2 border-t border-slate-800 flex items-center justify-between">
              <button
                id="iptv-settings-external-btn"
                data-focusable="true"
                type="button"
                onClick={handleLaunchExternalPlayer}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-md transition-all outline-none focus:ring-2 focus:ring-red-400"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Obrir a VLC / MX Player
              </button>

              <button
                id="iptv-settings-copy-btn"
                data-focusable="true"
                type="button"
                onClick={handleCopyStreamUrl}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 text-xs font-semibold rounded-xl border border-slate-800 outline-none focus:ring-2 focus:ring-red-500"
              >
                {copiedUrl ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                {copiedUrl ? 'Copiat' : 'Copiar URL'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Controls Overlay */}
      <div 
        className={`absolute inset-0 z-30 flex flex-col justify-between p-4 md:p-8 bg-gradient-to-t from-black/90 via-transparent to-black/80 transition-opacity duration-300 pointer-events-none ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Top Header Controls */}
        <div className="flex items-center justify-between pointer-events-auto">
          <div className="flex items-center gap-3">
            <button
              id="iptv-player-back-btn"
              data-focusable="true"
              onClick={onClose}
              className="p-2.5 rounded-xl bg-black/40 hover:bg-black/60 text-white backdrop-blur-md border border-white/10 transition-all hover:scale-105 active:scale-95 outline-none focus:ring-2 focus:ring-red-500"
              title="Tornar enrere"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <ChannelLogo
                logo={(item as any).logo || (item as any).poster}
                name={itemName}
                category={(item as any).category}
                userAgent={selectedUserAgent}
                size="sm"
                className="w-10 h-10 border border-white/10"
              />
              <div className="min-w-0 max-w-[200px] sm:max-w-md md:max-w-lg">
                <div className="flex items-center gap-2">
                  <MarqueeText
                    text={itemName}
                    className="text-base sm:text-lg font-bold text-white drop-shadow-md"
                  />
                  {isLive && (
                    <span className="shrink-0 flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-red-600/80 text-white text-[10px] font-bold uppercase tracking-wider">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      Directe
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-300 flex items-center gap-2 truncate">
                  {currentChannel?.category && <span>{currentChannel.category}</span>}
                  {itemSourceName && <span>• {itemSourceName}</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Native ExoPlayer Button (Visible when running on Android TV / APK) */}
            {(typeof window !== 'undefined' && Boolean((window as any).Android)) && (
              <button
                id="iptv-player-native-btn"
                data-focusable="true"
                onClick={handleLaunchNativePlayer}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600/80 hover:bg-emerald-600 text-white backdrop-blur-md border border-emerald-500/40 transition-all text-xs font-semibold outline-none focus:ring-2 focus:ring-emerald-400"
                title="Reproduir amb el reproductor natiu ExoPlayer"
              >
                <Play className="w-3.5 h-3.5 fill-current" />
                <span className="hidden sm:inline">ExoPlayer</span>
              </button>
            )}

            {/* External Player Button */}
            <button
              id="iptv-player-external-btn"
              data-focusable="true"
              onClick={handleLaunchExternalPlayer}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-black/40 hover:bg-red-600/80 text-white backdrop-blur-md border border-white/10 transition-all text-xs font-semibold outline-none focus:ring-2 focus:ring-red-500"
              title="Obrir amb VLC / MX Player extern"
            >
              <ExternalLink className="w-4 h-4 text-red-400" />
              <span className="hidden sm:inline">VLC / Extern</span>
            </button>

            {onToggleFavorite && (
              <button
                id="iptv-player-fav-btn"
                data-focusable="true"
                onClick={onToggleFavorite}
                className={`p-2.5 rounded-xl bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/10 transition-all outline-none focus:ring-2 focus:ring-red-500 ${
                  isFavorite ? 'text-amber-400' : 'text-white hover:text-amber-400'
                }`}
                title="Afegir a preferits"
              >
                <Star className="w-5 h-5" fill={isFavorite ? 'currentColor' : 'none'} />
              </button>
            )}

            {isLive && allChannels.length > 0 && (
              <button
                id="iptv-player-guide-btn"
                data-focusable="true"
                onClick={() => setShowChannelList(p => !p)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black/40 hover:bg-black/60 text-white backdrop-blur-md border border-white/10 transition-all text-xs font-semibold outline-none focus:ring-2 focus:ring-red-500"
                title="Llista de canals"
              >
                <List className="w-4 h-4 text-red-400" />
                <span className="hidden sm:inline">Canals</span>
              </button>
            )}

            <button
              id="iptv-player-settings-btn"
              data-focusable="true"
              onClick={() => setShowSettingsModal(true)}
              className="p-2.5 rounded-xl bg-black/40 hover:bg-black/60 text-white backdrop-blur-md border border-white/10 transition-all outline-none focus:ring-2 focus:ring-red-500"
              title="Configuració"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Center Zapping Quick Arrows for TV/Live */}
        {isLive && allChannels.length > 1 && (
          <div className="flex items-center justify-between w-full pointer-events-auto px-2">
            <button
              id="iptv-player-zap-prev-btn"
              data-focusable="true"
              onClick={(e) => { e.stopPropagation(); zapPrevChannel(); }}
              className="p-3 rounded-full bg-black/40 hover:bg-red-600/80 text-white backdrop-blur-md border border-white/10 transition-all hover:scale-110 active:scale-95 shadow-lg outline-none focus:ring-2 focus:ring-red-500"
              title="Canal anterior (Fletxa esquerra)"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              id="iptv-player-zap-next-btn"
              data-focusable="true"
              onClick={(e) => { e.stopPropagation(); zapNextChannel(); }}
              className="p-3 rounded-full bg-black/40 hover:bg-red-600/80 text-white backdrop-blur-md border border-white/10 transition-all hover:scale-110 active:scale-95 shadow-lg outline-none focus:ring-2 focus:ring-red-500"
              title="Canal següent (Fletxa dreta)"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* Bottom Control Bar */}
        <div className="flex items-center justify-between gap-4 pointer-events-auto bg-black/60 backdrop-blur-md p-3.5 rounded-2xl border border-white/10">
          {/* Play / Pause & Volume */}
          <div className="flex items-center gap-3">
            <button
              id="iptv-player-play-btn"
              data-focusable="true"
              onClick={togglePlay}
              className="p-2 rounded-xl bg-red-600 hover:bg-red-700 text-white transition-all hover:scale-105 active:scale-95 shadow-md shadow-red-600/20 outline-none focus:ring-2 focus:ring-red-400"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 fill-current" />}
            </button>

            <div className="flex items-center gap-2 group">
              <button
                id="iptv-player-mute-btn"
                data-focusable="true"
                onClick={toggleMute}
                className="p-2 text-slate-300 hover:text-white transition-colors outline-none focus:ring-2 focus:ring-red-500 rounded-lg"
              >
                {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <input
                id="iptv-player-volume-slider"
                data-focusable="true"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={isMuted ? 0 : volume}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setVolume(val);
                  if (videoRef.current) {
                    videoRef.current.volume = val;
                    videoRef.current.muted = false;
                    setIsMuted(false);
                  }
                }}
                className="w-16 sm:w-24 h-1.5 bg-slate-700 accent-red-500 rounded-lg cursor-pointer outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>

          {/* Player Settings & Format Controls */}
          <div className="flex items-center gap-2">
            <button
              id="iptv-player-aspect-btn"
              data-focusable="true"
              onClick={cycleAspectRatio}
              className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white text-xs font-mono font-medium border border-white/10 transition-colors uppercase outline-none focus:ring-2 focus:ring-red-500"
              title="Canviar relació d'aspecte (Fit, Fill, Stretch, 16:9, 4:3)"
            >
              {getAspectRatioLabel()}
            </button>

            <button
              id="iptv-player-reload-btn"
              data-focusable="true"
              onClick={() => setRetryCount(c => c + 1)}
              className="p-2 text-slate-300 hover:text-white transition-colors outline-none focus:ring-2 focus:ring-red-500 rounded-lg"
              title="Recarregar flux"
            >
              <RefreshCw className="w-4 h-4" />
            </button>

            <button
              id="iptv-player-fullscreen-btn"
              data-focusable="true"
              onClick={toggleFullscreen}
              className="p-2 text-slate-300 hover:text-white transition-colors outline-none focus:ring-2 focus:ring-red-500 rounded-lg"
              title="Pantalla completa"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
